import { useAuthStore } from '../stores/auth-store'
import { Router } from 'vue-router'
import { useApiClient } from './api-client'

// Note: Do NOT call useApiClient() at module level due to circular dependency with api-client.ts
// Instead, get the apiClient instance inside each function that needs it

/**
 * Login the user
 * @param username - Username of the user
 * @param password - Password of the user
 * @param router   - Router instance passed from the component
 */
export const login = async (username: string, password: string, router?: Router) => {
  const authStore = useAuthStore()

  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/User/Login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ Email: username, Password: password }),
    })

    if (!response.ok) {
      throw new Error('Login failed')
    }

    const data = await response.json()
    authStore.accessToken = data.accessToken
    authStore.refreshToken = data.refreshToken
    authStore.roles = data.roles
    // Redirect to home page if router is provided
    if (router) {
      router.push({ name: 'dashboard' })
    }
  } catch (error) {
    console.error('Error during login:', error)
    throw new Error('Login failed')
  }
}

export const refreshAccessToken = async () => {
  const authStore = useAuthStore()
  if (!authStore.refreshToken) {
    return null
  }

  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/User/RefreshToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ RefreshToken: authStore.refreshToken, AccessToken: authStore.accessToken }),
    })

    if (!response.ok) {
      throw new Error('Failed to refresh access token')
    }

    const data = await response.json()
    authStore.refreshToken = data.refreshToken
    authStore.accessToken = data.accessToken
    return data.accessToken
  } catch (error) {
    console.error('Error refreshing access token:', error)
    return null
  }
}

/**
 * Logout the user
 * @param router  - Router instance passed from the component
 */
export const logout = async (router?: Router, force: boolean = false) => {
  const authStore = useAuthStore()
  const apiClient = useApiClient()

  // Store token before clearing it for the background API call
  const currentAccessToken = authStore.accessToken

  // Call logout API in the background
  if (currentAccessToken && !force) {
    try {
      const response = await apiClient.post('/User/Logout', {
        headers: {
          Authorization: `Bearer ${currentAccessToken}`,
        },
      })

      if (!response.ok) {
        console.warn('Failed to logout on server, but local session was already cleared')
      }
    } catch (error) {
      console.error('Error while calling logout API in background:', error)
      // This is fine since the user is already logged out locally
    }
  }
  // Clear tokens and user data immediately
  authStore.accessToken = null
  authStore.refreshToken = null
  authStore.user = null
  authStore.roles = []

  // Redirect to login page immediately if router is provided
  if (router) {
    router.push({ name: 'login' })
  }
}

/**
 * Fetch user profile
 */
export const fetchUserProfile = async () => {
  const authStore = useAuthStore()
  const apiClient = useApiClient()

  if (!authStore.accessToken) {
    return null
  }

  try {
    const userProfile = await apiClient.get('/User/Info')
    console.log('Fetched user profile:', userProfile)
    authStore.user = userProfile
    return userProfile
  } catch (error) {
    console.error('Error fetching user profile:', error)
    return null
  }
}

/**
 * Fetch user status
 */
export const fetchUserStatus = async (): Promise<string | null> => {
  const apiClient = useApiClient()
  try {
    const status = await apiClient.get('/User/Status')
    return status
  } catch (error) {
    console.error('Error fetching user status:', error)
    return null
  }
}

/**
 * Change user status
 * @param status - New status value ('online', 'offline', 'away')
 */
export const changeUserStatus = async (status: 'online' | 'offline' | 'away'): Promise<boolean> => {
  const apiClient = useApiClient()
  try {
    await apiClient.post('/User/ChangeStatus', { status })
    return true
  } catch (error) {
    console.error('Error changing user status:', error)
    return false
  }
}
